import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { useState } from 'react';
import { Button, Loading } from 'react-daisyui';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { randomString } from '../../../../../../../../functions/string.functions.ts';
import { MediaType } from '../../../../../../Chat.enum.ts';
import {
  formatDate,
  formatTime,
  parseSlides,
} from '../../../../../../Chat.functions.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { tAttachment, tChatEvent } from '../../../../../../Chat.types.ts';
import useNotesAction from '../../../../../MessageBar/components/NotesAction/NotesAction.hook.ts';
import { isDocumentOrPdf } from '../../../../../MessageBar/components/Thumbnail/Thumbnail.functions.ts';
import AudioContent from '../MessageBubble/components/AudioContent/AudioContent.component.tsx';
import FileContent from '../MessageBubble/components/FileContent/FileContent.component.tsx';
import ImageContent from '../MessageBubble/components/ImageContent/ImageContent.component.tsx';
import LocationContent from '../MessageBubble/components/LocationContent/LocationContent.component.tsx';
import StickerContent from '../MessageBubble/components/StickerContent/StickerContent.component.tsx';
import VideoContent from '../MessageBubble/components/VideoContent/VideoContent.component.tsx';
import { useFeatureFlagsContext } from '../../../../../../../../providers/FeatureFlagsProvider/FeatureFlags.context';
import { SLB_FUNNEL_MOVE_NOTE_TITLE } from 'src/constants/smartleadbot.constants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  getContentFunnelMove,
  openFunnelBoard,
} from '../SystemBubble/components/FunnelMoveBubble/funnelMove.functions.ts';
import './NoteBubble.styles.scss';

export default function NoteBubble(note: tChatEvent) {
  const { t } = useTranslation('chat/chat');
  const navigate = useNavigate();
  const { selectedChat, setSelectedNote } = useChatContext();
  const { deleteEventNote, loading } = useNotesAction();
  const { isSmartLeadBotEnabled } = useFeatureFlagsContext();

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);
  const isSLBNote = isSmartLeadBotEnabled && note.createdBy === 'smart_leadbot';
  // RET-1311 — the durable funnel-move note reuses the lilac SLB treatment but
  // gets its own header. Matched by the stable persisted title.
  const isFunnelMoveNote =
    isSLBNote && note.note?.title === SLB_FUNNEL_MOVE_NOTE_TITLE;
  // RET-1311 — structured refs persisted with the note (messaging) power the
  // «Ver en embudo» deep-link on the durable record. Notes created before the
  // refs shipped lack them → CTA hidden, prose stands alone.
  const funnelMoveTargetUuid = isFunnelMoveNote
    ? getContentFunnelMove(note.content)?.to_funnel?.uuid
    : undefined;

  function onCloseLightbox() {
    setOpenLightbox(false);
  }

  function RenderFileType({ attachment }: { attachment: tAttachment }) {
    switch (attachment.mediaType) {
      case MediaType.IMAGE:
        return <ImageContent attachment={attachment} />;
      case MediaType.VIDEO:
        return <VideoContent attachment={attachment} />;
      case MediaType.AUDIO:
        return <AudioContent attachment={attachment} />;
      case MediaType.STICKER:
        return <StickerContent attachment={attachment} />;
      case MediaType.DOCUMENT:
        return <FileContent attachment={attachment} />;
      case MediaType.LOCATION:
        return (
          <LocationContent
            latitude={attachment.latitude ?? 0}
            longitude={attachment.longitude ?? 0}
          />
        );
      default:
        return <FileContent attachment={attachment} />;
    }
  }

  return (
    <div className={classNames('NoteBubble', { 'NoteBubble--slb': isSLBNote })}>
      <div className="NoteBubble__Header">
        <div className="NoteBubble__Header__Container">
          {isSLBNote ? (
            <div className="NoteBubble__Header__Container__SLBTitle">
              <SparklesIcon className="w-3.5 h-3.5 shrink-0" />
              <span>
                {isFunnelMoveNote
                  ? t('smartLeadbot.funnelMoveTitle')
                  : t('smartLeadbot.escalationNoteTitle')}
              </span>
            </div>
          ) : (
            <h1 className="NoteBubble__Header__Container__Title">
              {note?.note?.title ?? 'Nota'}
            </h1>
          )}
        </div>
        {!isSLBNote && <div className="NoteBubble__Header__Actions">
          <Button
            size="xs"
            color="ghost"
            shape="circle"
            onClick={() => {
              setSelectedNote({
                id: note.id,
                title: note.note?.title || '',
                content: note.content?.text || '',
                attachments: note.content?.attachments || undefined,
              });
            }}
          >
            <PencilIcon className="NoteBubble__Header__Actions__Icon" />
          </Button>
          <Button
            size="xs"
            color="ghost"
            shape="circle"
            onClick={() => {
              deleteEventNote(note.id);
            }}
          >
            {loading ? (
              <Loading className="NoteBubble__Header__Actions__Icon" />
            ) : (
              <TrashIcon className="NoteBubble__Header__Actions__Icon" />
            )}
          </Button>
        </div>}
      </div>
      <p className={classNames('break-words whitespace-pre-wrap', { 'text-xs': isSLBNote })}>{note.content?.text}</p>
      {funnelMoveTargetUuid && (
        <button
          type="button"
          className="NoteBubble__FunnelMoveCta"
          onClick={() => openFunnelBoard(navigate, funnelMoveTargetUuid)}
        >
          <span>{t('smartLeadbot.funnelMoveCta')}</span>
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 shrink-0" />
        </button>
      )}
      <div className="form-control gap-2">
        {note.content?.attachments?.map((attachment) => (
          <div
            key={randomString()}
            onClick={() => {
              if (
                attachment?.mimeType &&
                !isDocumentOrPdf(attachment?.mimeType)
              ) {
                setOpenLightbox(true);
              }
            }}
          >
            <RenderFileType attachment={attachment} />
          </div>
        ))}
        {openLightbox && (
          <Lightbox
            plugins={[Zoom, Captions, Thumbnails, Download, Fullscreen, Video]}
            open={openLightbox}
            close={onCloseLightbox}
            slides={parseSlides(note?.content?.attachments || [])}
          />
        )}
      </div>
      <div className={classNames('NoteBubble__Bottom', { 'NoteBubble__Bottom--slb': isSLBNote })}>
        {!isSLBNote && (
          <>
            <span className="NoteBubble__Header__Container__Title">
              {
                selectedChat?.lead?.users?.find(
                  (user) => user.auth_id === note.createdBy,
                )?.name
              }
            </span>
            |{' '}
          </>
        )}
        {formatDate(note?.createdAt ?? '')} | {formatTime(note?.createdAt ?? '')}
      </div>
    </div>
  );
}
